'use client';

import { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useTranslations } from '@/i18n/useTranslations';
import type { ToolCallEntry } from '@/stores/ai-chat-store';

export function AiToolCallCard({ tc }: { tc: ToolCallEntry }) {
  const t = useTranslations('ai');
  const [expanded, setExpanded] = useState(false);

  const displayText = tc.description || tc.label;
  const hasParams = tc.params && Object.keys(tc.params).length > 0;
  const hasResult = tc.result !== undefined && tc.result !== null;

  // Calling state
  if (tc.status === 'calling') {
    return (
      <div className="flex justify-start mb-2">
        <div className="max-w-[85%] rounded-lg bg-selected-soft border border-primary/50 px-3 py-2 flex items-center gap-2 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          <span className="text-primary dark:text-primary font-medium">{displayText}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (tc.status === 'error') {
    return (
      <div className="flex justify-start mb-2">
        <div className="max-w-[85%] rounded-lg bg-error-soft border border-error/20 px-3 py-2">
          <div className="flex items-center gap-2 text-xs">
            <XCircle className="h-3.5 w-3.5 text-error-text" />
            <span className="font-medium text-error-text">{displayText}</span>
            <span className="text-red-400">— {tc.error}</span>
          </div>
        </div>
      </div>
    );
  }

  // Done — approval pending
  const r = tc.result as Record<string, unknown> | undefined;
  if (r && 'approvalPending' in r && r.approvalPending) {
    return (
      <div className="flex justify-start mb-2">
        <div className="max-w-[85%] rounded-lg bg-warning-soft border border-warning/20 px-3 py-2 flex items-center gap-2 text-xs">
          <Clock className="h-3.5 w-3.5 text-warning-text" />
          <span className="text-warning-text font-medium">{displayText}</span>
          <span className="text-warning-text">— {t('waitingApproval')}</span>
        </div>
      </div>
    );
  }

  // Done — success with result
  const summary = buildSummary(tc.name, tc.result, t);
  const canExpand = hasParams || hasResult;

  return (
    <div className="flex justify-start mb-2">
      <div className="max-w-[85%] rounded-lg bg-muted/80 border border-border dark:border-border px-3 py-2">
        <button
          className="flex items-center gap-2 text-xs w-full text-left group"
          onClick={() => canExpand && setExpanded(!expanded)}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" />
          <span className="font-medium text-foreground">{displayText}</span>
          {summary && <span className="text-muted-foreground">— {summary}</span>}
          {canExpand && (
            expanded
              ? <ChevronDown className="h-3 w-3 ml-auto text-muted-foreground" />
              : <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </button>
        {expanded && canExpand && (
          <div className="mt-2 space-y-2">
            {hasParams && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{t('requestParams')}</div>
                <pre className="text-xs bg-overlay/50 rounded-md p-2 overflow-auto max-h-32 text-foreground border border-border dark:border-border">
                  {JSON.stringify(tc.params, null, 2)}
                </pre>
              </div>
            )}
            {hasResult && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{t('responseResult')}</div>
                <pre className="text-xs bg-overlay/50 rounded-md p-2 overflow-auto max-h-48 text-foreground border border-border dark:border-border">
                  {JSON.stringify(tc.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Legacy wrapper for backward compat
export function AiToolResult({ toolName, result }: { toolName: string; result: unknown }) {
  const t = useTranslations('ai');
  const summary = buildSummary(toolName, result, t);
  const [expanded, setExpanded] = useState(false);
  const hasData = result !== undefined && result !== null;

  return (
    <div className="flex justify-start mb-2">
      <div className="max-w-[85%] rounded-lg bg-muted/80 border border-border dark:border-border px-3 py-2">
        <button
          className="flex items-center gap-2 text-xs w-full text-left group"
          onClick={() => hasData && setExpanded(!expanded)}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" />
          <span className="font-medium text-foreground">{toolName}</span>
          {summary && <span className="text-muted-foreground">— {summary}</span>}
          {hasData && (
            expanded
              ? <ChevronDown className="h-3 w-3 ml-auto text-muted-foreground" />
              : <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </button>
        {expanded && hasData && (
          <pre className="mt-2 text-xs bg-overlay/50 rounded-md p-2 overflow-auto max-h-48 text-foreground border border-border dark:border-border">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function buildSummary(toolName: string, result: unknown, t: (key: string) => string): string {
  if (!result || typeof result !== 'object') return '';
  const r = result as Record<string, unknown>;

  if ('total' in r && typeof r.total === 'number') {
    return t('totalRecords').replace('{count}', String(r.total));
  }
  if (Array.isArray(r)) {
    return `${r.length} ${t('items')}`;
  }
  if ('data' in r && Array.isArray(r.data)) {
    return `${r.data.length} ${t('items')}`;
  }
  return t('dataRetrieved');
}
